import { FC, useState } from "react";
import styled from "styled-components";
import { AnimatePresence } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";

import thumbnailPlaceholder from "@/assets/icons/thumbnail-placeholder.svg";
import noChannelImg from "@/assets/icons/no-channel-placeholder.svg";
import Text from "@/components/Text";
import { Thumbnail } from "@/components/Table";
import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";
import { numFormatter } from "@/utils/helpers";
import NetworkIcon from "@/components/NetworkIcon";
import { hexToRgba } from "@/utils/theme";
import useAlerts from "@/hooks/useAlerts";
import { ReleasePredictionResource } from "@/types/resources";
import { api } from "@/utils/api";
import DistributionScheduleModal from "@/pages/DistributionSchedule/components/DistributionScheduleModal";

const Box = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: 16px 24px;
  margin-bottom: 16px;
`;

const BoxTop = styled.div`
  display: flex;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.lightGray};
  margin: 0 -24px;
  padding: 0 24px 16px;
  position: relative;
`;

const ActionsDropdownWrap = styled.div`
  position: absolute;
  top: 0;
  right: 12px;
`;

const BoxBottom = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 16px;
`;

const BoxTopLeft = styled.div`
  width: 50%;
  display: flex;
  padding-right: 30px;
`;

const BoxTopRight = styled.div`
  width: 50%;
  display: flex;
  padding-left: 22px;
  justify-content: flex-end;
`;

const BoxTopColumn = styled.div`
  width: 33.33%;
  &:last-child {
    padding-right: 16px;
  }
`;

const BoxBottomLeft = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
`;

const Image = styled.img`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 8px;
  flex-shrink: 0;
  border: 1px solid rgba(144, 144, 144, 0.3);
`;

const BoxBottomRight = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const ColumnTitle = styled(Text)`
  margin-bottom: 2px;
`;

const Time = styled(Text)`
  display: inline-block;
  background: ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  font-size: 11px;
  padding: 6px 12px 5px;
  min-width: 52px;
  text-align: center;
  min-width: 54px;
  > span {
    font-weight: 400;
  }
`;

const StyledThumbnail = styled(Thumbnail)`
  height: 49px;
  width: 89px;
  flex-shrink: 0;
`;

const Title = styled(Text)`
  margin-left: 16px;
`;

const StyledActionsDropdown = styled(ActionsDropdown)`
  width: 200px;
  z-index: 15;
`;

const Description = styled.div`
  display: flex;
  align-items: center;
  svg {
    position: absolute;
    top: -2px;
    left: -2px;
  }
`;

const DescriptionText = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ImageWrap = styled.div`
  position: relative;
  display: flex;
`;

const ScheduleButton = styled.button`
  padding: 0 16px;
  border-radius: 4px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  background: ${({ theme }) => hexToRgba(theme.colors.purple, 0.15)};
  &:hover {
    opacity: 0.8;
  }
`;

interface Props {
  prediction: ReleasePredictionResource;
}

const PredictionCard: FC<Props> = ({ prediction }) => {
  const [showForm, setShowForm] = useState(false);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onSkip = async () => {
    try {
      const confirmed = await alerts.confirm(
        "Would you like to skip this release?",
        prediction.title
      );

      if (!confirmed) return;

      await api.patch(`/v1/release-predictions/${prediction.id}/skip`);

      await queryClient.invalidateQueries(["release_predictions"]);

      alerts.success("Predictions skipped", prediction.title);
    } catch (_) {
      alerts.error();
    }
  };

  return (
    <>
      <Box>
        <BoxTop>
          <ActionsDropdownWrap>
            <StyledActionsDropdown>
              <ActionsDropdownButton onClick={onSkip} as="button">
                <span>
                  <svg
                    width="19"
                    height="16"
                    viewBox="0 0 19 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1.35693 8H14.9284"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8.82129 14L14.9284 8L8.82129 2"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M17.6426 14.6668V1.3335"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                Skip
              </ActionsDropdownButton>
            </StyledActionsDropdown>
          </ActionsDropdownWrap>
          <BoxTopLeft>
            <StyledThumbnail src={thumbnailPlaceholder} />
            <Title $type="smallBold">{prediction.title}</Title>
          </BoxTopLeft>
          <BoxTopRight>
            <BoxTopColumn>
              <ColumnTitle $type="smallBold">Fresh ID</ColumnTitle>
              <Text $type="smallMedium" $color="gray">
                {prediction.freshId}
              </Text>
            </BoxTopColumn>
            <BoxTopColumn>
              <ColumnTitle $type="smallBold">Project ID</ColumnTitle>
              <Text $type="smallMedium" $color="gray">
                {prediction.projectId}
              </Text>
            </BoxTopColumn>
            <BoxTopColumn>
              <ColumnTitle $type="smallBold">Expected Views</ColumnTitle>
              <Text $type="smallMedium" $color="gray">
                {numFormatter(prediction.expectedViews, 1)}
              </Text>
            </BoxTopColumn>
          </BoxTopRight>
        </BoxTop>
        <BoxBottom>
          <BoxBottomLeft>
            <Description>
              {prediction.channel ? (
                <>
                  <ImageWrap>
                    <Image
                      src={prediction.channel.thumbnail}
                      alt={prediction.channel.name + " thumbnail"}
                    />
                    <NetworkIcon type={prediction.channel.network} />
                  </ImageWrap>
                  <DescriptionText>
                    <Text $type="smallHeadline">{prediction.channel.name}</Text>
                  </DescriptionText>
                </>
              ) : (
                <>
                  <ImageWrap>
                    <Image
                      src={noChannelImg}
                      alt={"Channel icon placeholder"}
                    />
                  </ImageWrap>
                  <DescriptionText>
                    <Text $type="smallHeadline" $color="gray">
                      No channel
                    </Text>
                  </DescriptionText>
                </>
              )}
            </Description>
            <Time $type="smallBold">
              {DateTime.fromISO(prediction.createdAt).toFormat("dd/LL/yyyy")}
            </Time>
          </BoxBottomLeft>
          <BoxBottomRight>
            <ScheduleButton type="button" onClick={() => setShowForm(true)}>
              <Text as="span" $color="purple" $type="captionSemibold">
                Schedule
              </Text>
            </ScheduleButton>
          </BoxBottomRight>
        </BoxBottom>
      </Box>
      <AnimatePresence>
        {showForm && (
          <DistributionScheduleModal
            onClose={() => setShowForm(false)}
            prediction={prediction}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default PredictionCard;
